// Inline segment verification: hash each media segment as video.js's HLS
// loader receives it, and compare it POSITIONALLY against the sealed
// rendition manifest. Decision 4 of
// docs/plans/2026-08-13-verification-integration-closeout.md, and §13 of
// docs/plans/2026-08-10-verification-manifest.md.
//
// Three properties this file exists to hold, none of which is obvious from
// the code alone:
//
// 1. POSITION, NEVER URL. `RenditionManifest.segments[i].path` is a path
//    inside the *original* host's layout. A re-hosted video serves the same
//    bytes from completely different URLs — that is the whole reason the
//    manifest carries no absolute URLs (v2/manifest.go SourceInfo: "no
//    absolute URL appears in a manifest, so the proof survives a re-host").
//    So nothing here ever compares a manifest path against a request URL.
//    What it does instead: take the ORDER out of the media playlist VHS is
//    actually playing, and use the arriving URL only to look up which
//    position in that order it was. The index comes from the playlist; the
//    hash comes from the manifest at the same index.
//
// 2. PARSED PLAYLISTS, NEVER PLAYLIST BYTES. §13 notes that a CDN may
//    legitimately regenerate a `.m3u8` with different whitespace, so hashing
//    one would produce false failures. `notePlaylist` therefore takes VHS's
//    already-parsed playlist object and compares structure — segment count,
//    then durations — never text.
//
// 3. A FETCH OR DECODE ERROR IS NOT A MISMATCH. Only bytes that arrived and
//    hashed differently report `kind: 'mismatch'`. Everything else — an
//    errored request, an unreadable body, a playlist we cannot map — reports
//    `kind: 'network'`, the same discriminant verifyRoot.ts established. T16
//    stops playback on one and not the other, so conflating them would halt
//    a video on a CDN blip.
//
// This ticket (T15) only OBSERVES. It changes no playback behaviour at all:
// every outcome leaves via `onResult`. T16 owns what to do about them.
import { hexEqual, sha256Hex } from './hash.js'
import type { RenditionManifest, RenditionVerified } from './verifyRendition.js'
import type { RootVerified } from './verifyRoot.js'

// How far a playlist's EXTINF may drift from the sealed duration before it is
// worth remarking on. Both numbers come from the same EXTINF originally, so
// any difference is a re-packaging artefact (rounding, a different precision)
// — hence a generous tolerance and, when it is exceeded, an error rather than
// a mismatch. The segment hashes below are the real proof.
export const DURATION_TOLERANCE_SECONDS = 0.05

// Longest the lazy playlist re-read in handleSegmentResponse may run. A
// demuxed ladder fetches an audio segment for every video segment and none of
// them is in the index, so without this every audio segment would walk VHS's
// playlist list on the main thread.
const PLAYLIST_REFRESH_INTERVAL_MS = 1000

// The request type VHS tags a media-segment fetch with
// (@videojs/http-streaming's segment-loader: `requestType: 'segment'`). Init
// segments ('segment-media-initialization'), keys ('segment-key') and
// playlists ('hls-playlist') go past the same hook and are not covered by a
// rendition manifest, which lists media segments only.
const MEDIA_SEGMENT_REQUEST_TYPE = 'segment'

// ---------------------------------------------------------------------------
// The shapes this module borrows from video.js / VHS.
//
// Declared structurally rather than imported so the verifier can be tested
// without a real player, and so a VHS upgrade that moves a field is a
// compile-time conversation rather than a silent `undefined`.
// ---------------------------------------------------------------------------

/** One entry of a media playlist, as m3u8-parser produces it. */
export interface PlaylistSegment {
  uri?: string
  resolvedUri?: string
  duration?: number
  /** Present when the segment is a byte range of a larger file. See
   * `describeMappingObstacle` for why that is refused rather than handled. */
  byterange?: { length?: number; offset?: number }
}

/** A parsed media playlist. `attributes` carries the master playlist's
 * EXT-X-STREAM-INF fields for this variant, which is how a playlist is
 * matched to a sealed rendition. */
export interface ParsedPlaylist {
  uri?: string
  resolvedUri?: string
  attributes?: {
    RESOLUTION?: { width?: number; height?: number }
    BANDWIDTH?: number
    CODECS?: string
  }
  segments?: PlaylistSegment[]
}

/** The xhr object VHS passes to an onResponse hook. */
export interface VhsRequest {
  uri?: string
  requestType?: string
  aborted?: boolean
  responseType?: string
  response?: unknown
}

export type VhsResponseHook = (request: VhsRequest, error: unknown, response: unknown) => void

/** VHS's per-player xhr wrapper, once `setupXhrHooks_` has run. */
export interface VhsXhr {
  onResponse?: (callback: VhsResponseHook) => void
  offResponse?: (callback: VhsResponseHook) => void
}

/** The slice of a video.js Player this module drives. `tech` is typed loosely
 * on purpose — its real signature differs between video.js releases, and
 * qualityLevels.ts already reaches VHS through the same narrow cast. */
export interface VerifiablePlayer {
  on(event: string, handler: () => void): void
  off(event: string, handler: () => void): void
  tech(safety?: unknown): unknown
}

/**
 * Anything that can be hung off a player to watch segments arrive.
 *
 * `SegmentVerifier` is the real one; `createRealLedgerSource` (fixture/) is the
 * demo one. They share nothing but these two methods, and `VideoPlayer` calls
 * nothing else on either — so this is the honest type for the player's prop.
 * It exists because the alternative was a structural lie: the class carries
 * private fields, so a plain object could only be passed through it by casting,
 * and a cast is a comment the compiler cannot check.
 */
export interface SegmentSource {
  attach(player: VerifiablePlayer): void
  detach(): void
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface SegmentVerified {
  ok: true
  stage: 'segment'
  /** The sealed rendition's name, e.g. "1080p". */
  rendition: string
  /** Index into `RenditionManifest.segments`. */
  segmentIndex: number
  /** The computed hash, lowercase hex. */
  hash: string
  /** How many bytes were hashed to get it. Counted as none when absent. */
  bytes?: number
}

export interface SegmentVerificationFailure {
  ok: false
  stage: 'segment'
  /** The discriminant T13 established, and the reason this module exists in
   * the shape it does: 'mismatch' means bytes arrived and disagreed with what
   * the publisher sealed; 'network' means we never got trustworthy bytes to
   * compare. T16 halts playback on the first and not the second. */
  kind: 'network' | 'mismatch'
  detail: string
  /** Absent only when the failure could not be attributed to a rendition. */
  rendition?: string
  /** Absent when the failure is about a whole playlist rather than one
   * segment — a segment-count disagreement, for instance. */
  segmentIndex?: number
  /** Present only on a hash disagreement. */
  expectedHash?: string
  /** Present only on a hash disagreement. */
  actualHash?: string
}

export type SegmentVerificationResult = SegmentVerified | SegmentVerificationFailure

export interface SegmentVerifierOptions {
  /**
   * The verified root manifest. This is a PRECONDITION, expressed in the type
   * rather than in a comment: `RootVerified` is only ever produced by
   * `verifyRoot`'s success branch, so a SegmentVerifier cannot be constructed
   * before root.json has been checked against the chain's root_hash. Decision
   * 4: "Playback is only ever stopped once the root manifest itself has
   * verified against the chain. If the root cannot be verified ... no segment
   * checking is attempted — there is nothing trustworthy to compare against."
   *
   * It is load-bearing as well as ceremonial: only renditions this root names
   * are checked, so a rendition manifest belonging to a different video
   * cannot be smuggled in alongside.
   */
  root: RootVerified
  /** Renditions already verified against the root's RenditionRefs (T14). */
  renditions: RenditionVerified[]
  /** Called for every outcome. May throw — it is caught here, so a consumer
   * that blows up cannot reach VHS's xhr callback. */
  onResult: (result: SegmentVerificationResult) => void
}

// ---------------------------------------------------------------------------
// Pure helpers — the parts worth testing without a player
// ---------------------------------------------------------------------------

/** The playlist's RESOLUTION as the Go side spells it (`hls.Variant`:
 * `"1920x960"`, the raw attribute string), or null when absent. */
export function playlistResolution(playlist: ParsedPlaylist): string | null {
  const res = playlist.attributes?.RESOLUTION
  if (!res || typeof res.width !== 'number' || typeof res.height !== 'number') return null
  return `${res.width}x${res.height}`
}

/**
 * Which sealed rendition this playlist is, decided from its parsed identity
 * — never from its URL, which a re-host changes.
 *
 * Returns null rather than guessing. Two cases matter and both must be
 * skipped silently by the caller:
 *   - no identifying attributes at all (an audio-only rendition on a demuxed
 *     ladder, which the manifest does not cover); guessing would attribute
 *     audio bytes to a video rendition and report a false mismatch for every
 *     segment of the video.
 *   - an ambiguous match; picking one would make the segment lists of two
 *     quality levels interchangeable, the exact hazard the identity
 *     cross-check at api/pkg/verification/v2/manifest.go:68 exists to close.
 */
export function matchRendition(
  playlist: ParsedPlaylist,
  manifests: RenditionManifest[],
): RenditionManifest | null {
  const resolution = playlistResolution(playlist)
  const bandwidth = playlist.attributes?.BANDWIDTH
  if (resolution === null && typeof bandwidth !== 'number') return null
  const candidates = manifests.filter((m) => {
    if (resolution !== null && m.resolution !== resolution) return false
    if (typeof bandwidth === 'number' && m.bandwidth !== bandwidth) return false
    return true
  })
  return candidates.length === 1 ? candidates[0] : null
}

/**
 * Why this playlist's segments cannot be mapped to positions, or null when
 * they can. Both cases are reported as errors rather than mismatches: they
 * say "we cannot check this", not "the bytes changed".
 *
 * A byterange playlist is refused rather than supported because the publish
 * job hashes whole segment URLs (`hashSegment` in api/pkg/worker/handlers.go
 * fetches `r.urls[i]` entire), so a manifest hash covers a whole file. A
 * Range response's bytes could never equal it, and reporting that as a
 * mismatch would halt a perfectly good video.
 */
export function describeMappingObstacle(segments: PlaylistSegment[]): string | null {
  const uris: string[] = []
  for (const segment of segments) {
    if (segment.byterange) {
      return 'is served as byte ranges of a larger file, which the sealed manifest (whole-file hashes) cannot be compared against'
    }
    const uri = segment.resolvedUri || segment.uri
    if (!uri) return 'has a segment with no URL, so its position cannot be recovered from a request'
    uris.push(uri)
  }
  if (new Set(uris).size !== uris.length) {
    return 'repeats a segment URL, so an arriving response cannot be attributed to one position'
  }
  return null
}

/** The first EXTINF that disagrees with the sealed duration, or null. */
export function describeDurationDisagreement(
  segments: PlaylistSegment[],
  manifest: RenditionManifest,
): string | null {
  for (let i = 0; i < segments.length; i++) {
    const played = segments[i].duration
    const sealed = manifest.segments[i]?.duration
    if (typeof played !== 'number' || typeof sealed !== 'number') continue
    if (Math.abs(played - sealed) > DURATION_TOLERANCE_SECONDS) {
      return `segment ${i} runs ${played}s, sealed as ${sealed}s`
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// The verifier
// ---------------------------------------------------------------------------

interface SegmentSlot {
  rendition: string
  segmentIndex: number
  expectedHash: string
}

export class SegmentVerifier {
  private readonly manifests: RenditionManifest[]
  private readonly onResult: (result: SegmentVerificationResult) => void

  /** Segment URL → the position it occupies in a sealed rendition. Built from
   * playlist ORDER; the URL is only a lookup key, never compared to anything
   * in the manifest. */
  private readonly slots = new Map<string, SegmentSlot>()
  /** Playlists already processed, by `<uri>#<segmentCount>` so a reload with
   * the same shape is free and a genuinely different one is re-examined. */
  private readonly notedPlaylists = new Set<string>()
  /** Renditions whose positional mapping is not trustworthy; segments from
   * them are ignored rather than checked against a meaningless index. */
  private readonly abandoned = new Set<string>()
  /** Already-reported successes, so a seek that re-fetches a segment does not
   * re-notify. The bytes are still re-hashed — see handleSegmentResponse. */
  private readonly reported = new Map<string, Set<number>>()

  private player: VerifiablePlayer | null = null
  private readonly hookedXhrs = new Set<VhsXhr>()
  private readonly responseHook: VhsResponseHook
  private readonly hooksReadyListener: () => void
  private lastPlaylistRefresh = 0
  private detached = false

  constructor(options: SegmentVerifierOptions) {
    this.onResult = options.onResult
    // Only renditions the verified root names. A rendition manifest can
    // verify perfectly well against a RenditionRef from some OTHER video's
    // root; intersecting here means the set being checked is the set the
    // chain agreed to. A rendition present in the root but absent from
    // `renditions` is simply not checked — T14 could not verify it, and
    // unverified is never treated as verified.
    const named = new Set(options.root.manifest.renditions.map((ref) => ref.name))
    this.manifests = options.renditions
      .map((verified) => verified.manifest)
      .filter((manifest) => named.has(manifest.name))

    this.responseHook = (request, error) => this.onVhsResponse(request, error)
    this.hooksReadyListener = () => this.registerResponseHook()
  }

  /**
   * Start observing a player's HLS loader.
   *
   * VHS creates a fresh handler — and a fresh xhr object — for every source,
   * and announces each one with `xhr-hooks-ready`. PlaybackCore keeps one
   * player for the whole mount and swaps streams through `setSource`, so
   * binding once would go quiet after the first sphere <-> quadrant crossing;
   * hence the listener as well as the immediate attempt.
   *
   * Registration is idempotent by construction: VHS keeps its hooks in a Set
   * and this passes the same bound function every time.
   */
  attach(player: VerifiablePlayer): void {
    this.player = player
    this.detached = false
    try {
      player.on('xhr-hooks-ready', this.hooksReadyListener)
    } catch {
      // A player that cannot take listeners gives us whatever the immediate
      // registration below finds, and nothing after that.
    }
    this.registerResponseHook()
  }

  /** Stop observing, and stop reporting — including from digests already in
   * flight, whose results would otherwise land after unmount. */
  detach(): void {
    this.detached = true
    const player = this.player
    this.player = null
    try {
      player?.off('xhr-hooks-ready', this.hooksReadyListener)
    } catch {
      // player already disposed
    }
    for (const xhr of this.hookedXhrs) {
      try {
        xhr.offResponse?.(this.responseHook)
      } catch {
        // tech already disposed
      }
    }
    this.hookedXhrs.clear()
  }

  /**
   * Take the segment ORDER out of one parsed media playlist.
   *
   * This is where the manifest design's "compare playlists by parsed
   * structure, never by hashing the .m3u8" rule lives: the argument is
   * already-parsed structure, so whitespace and formatting differences in the
   * source document cannot reach any comparison. Idempotent.
   */
  notePlaylist(playlist: ParsedPlaylist): void {
    const segments = playlist.segments ?? []
    // A playlist object VHS knows about but has not loaded yet. It will come
    // back with segments; the signature below lets it be re-examined then.
    if (segments.length === 0) return
    const key = playlist.resolvedUri || playlist.uri
    if (!key) return
    const signature = `${key}#${segments.length}`
    if (this.notedPlaylists.has(signature)) return
    this.notedPlaylists.add(signature)

    const manifest = matchRendition(playlist, this.manifests)
    // Outside the sealed manifest: an audio rendition on a demuxed ladder, an
    // I-frame playlist, a rung this video was never published with. Silence is
    // correct — none of it is evidence of anything.
    if (!manifest) return

    if (segments.length !== manifest.segments.length) {
      // A different number of segments is not a formatting difference. The
      // bytes being served are not the bytes that were sealed, and every
      // index after the first divergence would compare the wrong pair — so
      // report it and map nothing.
      this.abandoned.add(manifest.name)
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'mismatch',
        detail: `rendition "${manifest.name}" is being served as ${segments.length} segments; ${manifest.segments.length} were sealed`,
        rendition: manifest.name,
      })
      return
    }

    const obstacle = describeMappingObstacle(segments)
    if (obstacle) {
      this.abandoned.add(manifest.name)
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'network',
        detail: `rendition "${manifest.name}" ${obstacle}`,
        rendition: manifest.name,
      })
      return
    }

    const drift = describeDurationDisagreement(segments, manifest)
    if (drift) {
      // Reported, but verification continues: a re-packaged EXTINF says
      // nothing about the bytes, and the hashes below settle that question
      // properly. Calling this a mismatch would stop playback over a rounding
      // difference — precisely the false positive Decision 4 guards against.
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'network',
        detail: `rendition "${manifest.name}" playlist durations differ from the sealed manifest: ${drift}`,
        rendition: manifest.name,
      })
    }

    segments.forEach((segment, index) => {
      const uri = segment.resolvedUri || segment.uri
      if (!uri) return
      this.slots.set(uri, {
        rendition: manifest.name,
        segmentIndex: index,
        expectedHash: manifest.segments[index].hash,
      })
    })
  }

  /**
   * Check one segment response.
   *
   * ORDERING IS LOAD-BEARING HERE. Everything before `sha256Hex` is
   * synchronous, and `sha256Hex` calls `crypto.subtle.digest` immediately —
   * which, per WebCrypto, takes a copy of the bytes at call time. VHS hands
   * the same ArrayBuffer to its transmuxer worker and may TRANSFER it, which
   * detaches the buffer; awaiting anything before the digest call would leave
   * us hashing a zero-length corpse. Do not insert an `await` above it.
   *
   * It is also how the hashing stays off the render loop: the digest itself
   * runs in the browser's crypto implementation rather than on the main
   * thread, the caller gets control back before it resolves (VHS's xhr
   * callback runs on the main thread next to the 360° scene's render loop),
   * and the continuation does nothing heavier than a 32-byte hex compare. The
   * returned promise exists for tests; the hook deliberately ignores it.
   */
  async handleSegmentResponse(input: {
    uri: string
    bytes: ArrayBuffer | null
    /** The request errored — a 5xx, a timeout, a rejected connection. */
    failed?: boolean
    /** The request was aborted, which VHS does routinely on ABR switches and
     * seeks. Not a failure and not worth reporting. */
    aborted?: boolean
  }): Promise<void> {
    if (this.detached || input.aborted) return

    let slot = this.slots.get(input.uri)
    if (!slot) {
      // The playlist this segment came from may have loaded since the last
      // look. Throttled: on a demuxed ladder every audio segment lands here
      // and none of them will ever be in the index.
      this.refreshPlaylists()
      slot = this.slots.get(input.uri)
    }
    if (!slot) return
    if (this.abandoned.has(slot.rendition)) return

    if (input.failed) {
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'network',
        detail: `segment ${slot.segmentIndex} of rendition "${slot.rendition}" could not be fetched`,
        rendition: slot.rendition,
        segmentIndex: slot.segmentIndex,
      })
      return
    }

    const body = input.bytes
    if (!(body instanceof ArrayBuffer)) {
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'network',
        detail: `segment ${slot.segmentIndex} of rendition "${slot.rendition}" arrived in a form this browser could not read as bytes`,
        rendition: slot.rendition,
        segmentIndex: slot.segmentIndex,
      })
      return
    }

    // Synchronous call, awaited afterwards. See the note above. The length is
    // read here for the same reason: after the await the buffer may be detached.
    const digest = sha256Hex(body)
    const bytes = body.byteLength

    let actualHash: string
    try {
      actualHash = await digest
    } catch (err) {
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'network',
        detail: `segment ${slot.segmentIndex} of rendition "${slot.rendition}" could not be hashed: ${err instanceof Error ? err.message : 'unknown error'}`,
        rendition: slot.rendition,
        segmentIndex: slot.segmentIndex,
      })
      return
    }
    if (this.detached) return

    if (!hexEqual(actualHash, slot.expectedHash)) {
      this.emit({
        ok: false,
        stage: 'segment',
        kind: 'mismatch',
        detail: `segment ${slot.segmentIndex} of rendition "${slot.rendition}" does not match the bytes the publisher sealed`,
        rendition: slot.rendition,
        segmentIndex: slot.segmentIndex,
        expectedHash: slot.expectedHash,
        actualHash,
      })
      return
    }

    // A seek re-fetches segments that already passed. Re-hashing them is
    // nearly free and catches a swap mid-session, but re-announcing each one
    // would spray identical results at whatever renders them.
    let seen = this.reported.get(slot.rendition)
    if (!seen) {
      seen = new Set()
      this.reported.set(slot.rendition, seen)
    }
    if (seen.has(slot.segmentIndex)) return
    seen.add(slot.segmentIndex)

    this.emit({
      ok: true,
      stage: 'segment',
      rendition: slot.rendition,
      segmentIndex: slot.segmentIndex,
      hash: actualHash,
      bytes,
    })
  }

  // --- internals ----------------------------------------------------------

  private emit(result: SegmentVerificationResult): void {
    if (this.detached) return
    try {
      this.onResult(result)
    } catch {
      // A consumer that throws must not unwind through VHS's xhr callback and
      // take the segment loader down with it.
    }
  }

  private onVhsResponse(request: VhsRequest, error: unknown): void {
    if (this.detached) return
    if (request?.requestType !== MEDIA_SEGMENT_REQUEST_TYPE) return
    const uri = request.uri
    if (!uri) return
    // The promise is deliberately dropped: this runs on the main thread and
    // must return before the digest resolves.
    void this.handleSegmentResponse({
      uri,
      bytes: (request.response ?? null) as ArrayBuffer | null,
      failed: Boolean(error),
      aborted: Boolean(request.aborted),
    })
  }

  private registerResponseHook(): void {
    if (this.detached) return
    const xhr = this.vhs()?.xhr
    if (!xhr || typeof xhr.onResponse !== 'function') return
    if (this.hookedXhrs.has(xhr)) return
    this.hookedXhrs.add(xhr)
    try {
      xhr.onResponse(this.responseHook)
    } catch {
      this.hookedXhrs.delete(xhr)
    }
  }

  private vhs(): { xhr?: VhsXhr; playlists?: unknown } | null {
    const player = this.player
    if (!player) return null
    try {
      const tech = player.tech({ IWillNotUseThisInPlugins: true }) as { vhs?: { xhr?: VhsXhr; playlists?: unknown } } | null
      return tech?.vhs ?? null
    } catch {
      // No tech yet, or none at all: Safari and iOS play HLS natively (see
      // shouldUseNativeHls in player/core/PlaybackCore.ts), so there is no
      // loader to hook and nothing gets verified. That is a real gap, not an
      // error to raise into a mount effect.
      return null
    }
  }

  /** Re-read VHS's parsed playlists. Cheap after the first time — notePlaylist
   * early-returns on a signature it has already seen. */
  private refreshPlaylists(): void {
    const now = Date.now()
    if (now - this.lastPlaylistRefresh < PLAYLIST_REFRESH_INTERVAL_MS) return
    this.lastPlaylistRefresh = now
    const playlists = this.vhs()?.playlists as
      | {
          main?: { playlists?: ParsedPlaylist[] }
          master?: { playlists?: ParsedPlaylist[] }
          media?: () => ParsedPlaylist | undefined
        }
      | undefined
    if (!playlists) return
    try {
      // `main` since VHS 2.15, `master` before it.
      const main = playlists.main ?? playlists.master
      for (const playlist of main?.playlists ?? []) this.notePlaylist(playlist)
      const active = playlists.media?.()
      if (active) this.notePlaylist(active)
    } catch {
      // VHS's internal shapes differ across releases; a playlist we cannot
      // read just means those segments go unverified.
    }
  }
}

/**
 * Attach a verifier to a player, or — when there is no verifier — do NOTHING.
 *
 * This is the regression guard for every existing playback path, and the
 * reason it is a function rather than an `if` inside VideoPlayer: with no
 * verifier supplied, the player object is not read, not listened to, and not
 * reached into. It is not "a verifier that always passes"; nothing happens at
 * all. `__tests__/SegmentVerifier.test.ts` asserts that by counting
 * interactions with a spy player and requiring zero.
 *
 * Returns a teardown for React's effect cleanup, or undefined when there was
 * nothing to tear down.
 */
export function attachSegmentVerifier(
  verifier: SegmentSource | undefined,
  player: VerifiablePlayer,
): (() => void) | undefined {
  if (!verifier) return undefined
  verifier.attach(player)
  return () => verifier.detach()
}
